/**
 * ChoicePanel
 *
 * Choice tiles under the choice prompt, or under the last spoken line when there is no prompt.
 */

import { memo, type CSSProperties, type KeyboardEvent } from 'react';
import { getVisibleTextFromInlineTerms } from '../utils/inline-terms';
import { ChoiceOption } from '../types';
import { DialogueBox } from './dialogue-box';
import { FormattedInlineText } from './formatted-inline-text';
import styles from './choice-panel.module.css';

export interface StickyChoiceDialogue {
  speaker?: string;
  text: string;
  isInternal?: boolean;
}

interface ChoicePanelProps {
  prompt?: string;
  options: ChoiceOption[];
  onChoice: (index: number) => void;
  evaluateCondition: (condition: string) => boolean;
  compact?: boolean;
  elevated?: boolean;
  stickyDialogue?: StickyChoiceDialogue | null;
  uiFade?: 'in' | 'out';
}

export function isGenericChoicePrompt(prompt?: string): boolean {
  return !prompt || /vous r[ée]pondez/i.test(prompt);
}

function handleChoiceKeyDown(
  event: KeyboardEvent<HTMLDivElement>,
  onActivate: () => void,
) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onActivate();
  }
}

export const ChoicePanel = memo(({
  prompt,
  options,
  onChoice,
  evaluateCondition,
  compact = false,
  elevated = false,
  stickyDialogue = null,
  uiFade = 'in',
}: ChoicePanelProps) => {
  const visibleOptions = options.map((option, index) => ({
    option,
    index,
    visible:
      !option.stopButton
      && (!option.condition || evaluateCondition(option.condition))
  })).filter(item => item.visible);

  const showPrompt = !isGenericChoicePrompt(prompt);
  const shownSticky = showPrompt ? null : stickyDialogue;
  const tileCount = Math.min(Math.max(visibleOptions.length, 1), 3);

  const tiles = (
    <div
      className={compact ? styles.choiceButtons : styles.tiles}
      role="group"
      aria-labelledby={showPrompt ? 'choice-prompt' : undefined}
      aria-label={showPrompt ? undefined : 'Choix de réponse'}
      style={compact ? undefined : { '--tile-count': tileCount } as CSSProperties}
    >
      {visibleOptions.map(({ option, index }) => (
        <div
          key={index}
          role="button"
          tabIndex={0}
          className={compact ? styles.choiceButton : styles.tile}
          onClick={() => onChoice(index)}
          onKeyDown={(event) => handleChoiceKeyDown(event, () => onChoice(index))}
          aria-label={`Select option: ${getVisibleTextFromInlineTerms(option.text)}`}
        >
          {compact ? (
            <FormattedInlineText text={option.text} />
          ) : (
            <>
              {/* U+FE0E : rendu texte, pas émoji */}
              <span className={styles.tileMarker} aria-hidden>
                {'▶︎'}
              </span>
              <FormattedInlineText text={option.text} className={styles.tileText} />
            </>
          )}
        </div>
      ))}
    </div>
  );

  if (compact) {
    return (
      <div
        className={`${styles.choicePanel} ${styles.choicePanelMini} ${elevated ? styles.choicePanelElevated : ''} ${uiFade === 'out' ? styles.uiFadeOut : styles.uiFadeIn}`}
        role="group"
        aria-labelledby={showPrompt ? 'choice-prompt' : undefined}
      >
        {showPrompt && (
          <div id="choice-prompt" className={styles.choicePrompt}>
            <FormattedInlineText text={prompt!} />
          </div>
        )}
        {tiles}
      </div>
    );
  }

  return (
    <div
      className={`${styles.bottom} ${elevated ? styles.bottomElevated : ''} ${uiFade === 'out' ? styles.uiFadeOut : styles.uiFadeIn}`}
      role="group"
      aria-label="Choix de réponse"
    >
      {shownSticky && (
        <DialogueBox
          speaker={shownSticky.speaker}
          text={shownSticky.text}
          onClick={() => undefined}
          showContinue={false}
          isInternal={shownSticky.isInternal}
          keyboardActive={false}
          enableTyping={false}
          className={styles.embeddedDialogue}
          uiFade={uiFade}
        />
      )}

      {showPrompt && (
        <div id="choice-prompt" className={styles.promptBar}>
          <FormattedInlineText text={prompt!} />
        </div>
      )}

      {tiles}
    </div>
  );
});

ChoicePanel.displayName = 'ChoicePanel';
